/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/exportFieldSelector.scss';

import {ChangeFunction, Checkbox, CloseButton, WithBackground} from '@dvrd/dvr-controls';

import React, {ForwardedRef, forwardRef, useImperativeHandle, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ActivateHandle} from '../../../util/interfaces';

import {BluemateButton} from '../../controls';

import {invoiceFields, orderFields} from '../../../util/exportHelper';

interface Props {
    onSubmitFields: (invoiceFields: Array<string>, orderFields: Array<string>) => void;
}

function ExportFieldsSelector(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const {onSubmitFields} = props;
    const [selectedInvoiceFields, setInvoiceFields] = useState<Array<string>>([]);
    const [selectedOrderFields, setOrderFields] = useState<Array<string>>([]);
    const [active, setActive] = useState(false);

    function onActivate(_invoiceFields: Array<string>, _orderFields: Array<string>) {
        setInvoiceFields(_invoiceFields);
        setOrderFields(_orderFields);
        setActive(true);
    }

    function onClose() {
        setActive(false);
    }

    function onSubmit() {
        onSubmitFields(selectedInvoiceFields, selectedOrderFields);
        onClose();
    }

    function onSelectInvoiceField(fieldName: string) {
        return function () {
            const _invoiceFields = selectedInvoiceFields.slice();
            if (_invoiceFields.includes(fieldName)) _invoiceFields.splice(_invoiceFields.indexOf(fieldName), 1);
            else _invoiceFields.push(fieldName);
            setInvoiceFields(_invoiceFields);
        };
    }

    function onSelectOrderField(fieldName: string) {
        return function () {
            const _orderFields = selectedOrderFields.slice();
            if (_orderFields.includes(fieldName)) _orderFields.splice(_orderFields.indexOf(fieldName), 1);
            else _orderFields.push(fieldName);
            setOrderFields(_orderFields);
        };
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <div className='fields-container'>
                    <label className='container-title'>{t('settings.export.invoiceFields', 'Factuur velden')}</label>
                    <div className='fields'>
                        {Object.keys(invoiceFields).map(
                            renderField(selectedInvoiceFields, onSelectInvoiceField, invoiceFields)
                        )}
                    </div>
                </div>
                <div className='fields-container'>
                    <label className='container-title'>{t('settings.export.orderFields', 'Factuurregel velden')}</label>
                    <div className='fields'>
                        {Object.keys(orderFields).map(
                            renderField(selectedOrderFields, onSelectOrderField, orderFields)
                        )}
                    </div>
                </div>
            </div>
        );
    }

    function renderField(
        selected: Array<string>,
        onSelect: (fieldName: string) => ChangeFunction,
        translations: Record<string, string>
    ) {
        return function (fieldName: string, index: number) {
            const checked = selected.includes(fieldName);
            return (
                <Checkbox key={index} checked={checked} onCheck={onSelect(fieldName)} label={translations[fieldName]} />
            );
        };
    }

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='export-fields-selector default-popup'>
                <div className='header-container'>
                    <label className='header-title'>
                        {t('settings.export.selectFieldsTitle', 'Exportvelden selecteren')}
                    </label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
                <div className='footer-container btn-2'>
                    <BluemateButton onClick={onClose} label={t('settings.export.cancel', 'Annuleren')} secondary />
                    <BluemateButton onClick={onSubmit} label={t('settings.export.apply', 'Toepassen')} />
                </div>
            </div>
        </WithBackground>
    );
}

export default forwardRef<ActivateHandle, Props>(ExportFieldsSelector);
